import { useRouter } from 'found';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import { useI18n } from './I18nProvider';
import {
  getConfiguredFullLocale,
  resolveLocaleFallbackOrNull,
} from './handler';
import { LOCALE_QUERY_KEY, buildLocaleQuery, parseSearch } from './localeUrl';

const getCurrentHistoryState = (fallbackState) => {
  const browserState = window.history.state;
  return browserState
    && typeof browserState === 'object'
    && Object.prototype.hasOwnProperty.call(browserState, 'state')
    ? browserState.state
    : fallbackState;
};

/**
 * Keeps provider state synchronized with the locale represented by the route URL.
 * @param {object} props component props.
 * @param {React.ReactNode} props.children route children.
 * @returns {React.ReactElement} unchanged route children.
 */
export function LocaleUrlSynchronizer({ children }) {
  const routerContext = useRouter();
  const { updateLocaleFunc } = useI18n();
  const { match, router } = routerContext || {};
  const location = match?.location;
  const query = location?.query || {};
  const normalizedQuery = useMemo(() => buildLocaleQuery({ destinationQuery: query }), [query]);
  const explicitLocale = normalizedQuery[LOCALE_QUERY_KEY];
  const resolvedLocale = explicitLocale || getConfiguredFullLocale();
  const lastLocationRef = useRef(null);
  const localeNeedsCanonicalization = Object.prototype.hasOwnProperty.call(query, LOCALE_QUERY_KEY)
    && query[LOCALE_QUERY_KEY] !== explicitLocale;

  useEffect(() => {
    // Apply the URL locale only for a newly published location. A doNotRerunMatch
    // replacement leaves `location` stale, so reapplying it on other updates would
    // revert useUrlI18n switches. The provider ignores repeats of the current or
    // pending locale and cancels a pending load when a different one is requested.
    if (lastLocationRef.current !== location) {
      lastLocationRef.current = location;
      updateLocaleFunc(resolvedLocale);
    }
    if (localeNeedsCanonicalization && router && location) {
      router.replace({
        ...location,
        doNotRerunMatch: true,
        query: normalizedQuery,
      });
    }
  }, [
    location,
    localeNeedsCanonicalization,
    normalizedQuery,
    resolvedLocale,
    router,
    updateLocaleFunc,
  ]);

  return children;
}

LocaleUrlSynchronizer.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Returns i18n state with a router-aware locale update operation for UI controls.
 *
 * The URL replacement uses `doNotRerunMatch`, which Found 1.3 applies by mutating
 * the stored match without publishing a new router context, so
 * `LocaleUrlSynchronizer` cannot observe it. The locale is therefore also applied
 * directly. The next published location, including Back to the previous locale,
 * is applied again by the synchronizer.
 * @returns {object} i18n state and URL-backed updateLocaleFunc.
 */
export function useUrlI18n() {
  const i18n = useI18n();
  const { updateLocaleFunc: applyLocaleFunc } = i18n;
  const routerContext = useRouter();
  const { match, router } = routerContext || {};

  const updateLocaleFunc = useCallback((nextFullLocale) => {
    const resolvedLocale = resolveLocaleFallbackOrNull(nextFullLocale);
    if (!resolvedLocale) {
      throw new RangeError(`Unsupported locale: ${nextFullLocale}`);
    }
    if (!router) {
      throw new Error('useUrlI18n must be used within a Found router');
    }

    const liveQuery = parseSearch(window.location.search);
    router.replace({
      doNotRerunMatch: true,
      hash: window.location.hash,
      pathname: window.location.pathname,
      query: buildLocaleQuery({
        destinationQuery: {
          ...liveQuery,
          [LOCALE_QUERY_KEY]: resolvedLocale.fullLocale,
        },
      }),
      state: getCurrentHistoryState(match?.location?.state),
    });
    return applyLocaleFunc(resolvedLocale.fullLocale);
  }, [applyLocaleFunc, match, router]);

  return { ...i18n, updateLocaleFunc };
}
